import {
  collectGuideTourModalHosts,
  getActiveGuideTourModalHosts,
  isAnyGuideTourModalActive,
  getGuideTourStepModalVisibleGate,
} from '../routing/activation'
import { hasMoreUnseenRouteToursAfter } from '../routing/queue'
import type { GuideTourDefinition, GuideTourRegistry } from '../types'
import { findGuideTourTargetElement } from '../overlay/utils'
import {
  clearGuideTourRouteScrollState,
  getGuideTourScrollRestoreTarget,
  forceClickGuideTourElement,
} from './targets'
import { logGuideTourDebug } from './tourRuntime'

type GuideTourModalCloseHandler = () => void

const modalCloseHandlers = new Set<GuideTourModalCloseHandler>()

export const getGuideTourModalCloseHandlerCount = (): number =>
  modalCloseHandlers.size

export const registerGuideTourModalCloseHandler = (
  handler: GuideTourModalCloseHandler
): (() => void) => {
  modalCloseHandlers.add(handler)

  return () => {
    modalCloseHandlers.delete(handler)
  }
}

export const notifyGuideTourModalCloseHandlers = (): void => {
  for (const handler of modalCloseHandlers) {
    handler()
  }
}

export const resetGuideTourModalCloseHandlersForTests = (): void => {
  modalCloseHandlers.clear()
}

export const GUIDE_TOUR_REQUEST_CLOSE_MODALS_EVENT =
  'guide-tour:request-close-modals' as const

/** Notifies React-controlled modals to close when the tour exits a modal flow. */
export const requestGuideTourModalsClose = (): void => {
  const handlerCount = getGuideTourModalCloseHandlerCount()

  logGuideTourDebug('requestGuideTourModalsClose', {
    handlerCount,
  })

  notifyGuideTourModalCloseHandlers()

  if (typeof window === 'undefined') {
    return
  }

  window.dispatchEvent(new CustomEvent(GUIDE_TOUR_REQUEST_CLOSE_MODALS_EVENT))
}

const HEXA_DIALOG_HOSTS = new Set([
  'hexa-v2-dialog',
  'hexa-v2-modal',
  'hexa-v2-drawer',
])

const HEXA_CLICKABLE_CLOSE_HOSTS = new Set([
  'hexa-v2-button',
  'hexa-v2-icon-button',
  'button',
])

const CLOSE_TARGET_ID_PATTERN = /cancel|close|fechar|dismiss|voltar/i

export type GuideTourModalCloseAction =
  | { type: 'hexa-dialog-close'; host: HTMLElement }
  | { type: 'click'; element: HTMLElement }

const isVisibleGuideTourCloseElement = (element: HTMLElement): boolean => {
  if (!element.isConnected) {
    return false
  }

  if (element.hidden || element.getAttribute('aria-hidden') === 'true') {
    return false
  }

  const style = window.getComputedStyle(element)

  return style.display !== 'none' && style.visibility !== 'hidden'
}

const matchesGuideTourCloseTargetId = (id: string): boolean =>
  CLOSE_TARGET_ID_PATTERN.test(id)

const resolveClickableCloseHost = (
  element: HTMLElement
): HTMLElement | null => {
  const tag = element.tagName.toLowerCase()

  if (HEXA_CLICKABLE_CLOSE_HOSTS.has(tag)) {
    return element
  }

  const closest = element.closest('hexa-v2-button, hexa-v2-icon-button, button')

  return closest instanceof HTMLElement ? closest : null
}

const walkHostElements = (
  root: ParentNode,
  visit: (element: HTMLElement) => void
): void => {
  const elements =
    root instanceof HTMLElement || root instanceof DocumentFragment
      ? root.querySelectorAll('*')
      : []

  for (const node of elements) {
    if (!(node instanceof HTMLElement)) {
      continue
    }

    visit(node)

    if (node.shadowRoot) {
      walkHostElements(node.shadowRoot, visit)
    }
  }
}

const collectDialogFooterCancelButtons = (host: HTMLElement): HTMLElement[] => {
  const buttons: HTMLElement[] = []

  for (const element of host.querySelectorAll('hexa-v2-button')) {
    if (
      element instanceof HTMLElement &&
      isVisibleGuideTourCloseElement(element)
    ) {
      buttons.push(element)
    }
  }

  if (buttons.length === 0) {
    return []
  }

  const secondaryButtons = buttons.filter(element => {
    const variant = element.getAttribute('variant')

    if (variant === 'secondary') {
      return true
    }

    const className = element.getAttribute('class') ?? ''

    return className.includes('secondary')
  })

  if (secondaryButtons.length > 0) {
    return secondaryButtons
  }

  if (buttons.length >= 2) {
    return [buttons[0]]
  }

  return []
}

const collectCloseClickElements = (host: HTMLElement): HTMLElement[] => {
  const results: HTMLElement[] = []
  const seen = new Set<HTMLElement>()

  const add = (element: HTMLElement | null) => {
    if (!element || seen.has(element)) {
      return
    }

    const clickable = resolveClickableCloseHost(element)

    if (
      !clickable ||
      !HEXA_CLICKABLE_CLOSE_HOSTS.has(clickable.tagName.toLowerCase()) ||
      !isVisibleGuideTourCloseElement(clickable)
    ) {
      return
    }

    seen.add(clickable)
    results.push(clickable)
  }

  walkHostElements(host, element => {
    const markers = [element.id, element.dataset.guideTourId].filter(
      (value): value is string => Boolean(value)
    )

    for (const marker of markers) {
      if (!matchesGuideTourCloseTargetId(marker)) {
        continue
      }

      add(element)
      add(findGuideTourTargetElement(marker, host))
    }
  })

  if (HEXA_DIALOG_HOSTS.has(host.tagName.toLowerCase())) {
    for (const element of collectDialogFooterCancelButtons(host)) {
      add(element)
    }
  }

  return results
}

const findShadowHeaderCloseButton = (host: HTMLElement): HTMLElement | null => {
  const shadowRoot = host.shadowRoot

  if (!shadowRoot) {
    return null
  }

  const selectors = [
    'hexa-v2-icon-button',
    'button[aria-label*="close" i]',
    'button[aria-label*="fechar" i]',
    'button[data-testid*="close" i]',
    'button.close',
  ]

  for (const selector of selectors) {
    const candidate = shadowRoot.querySelector(selector)

    if (
      candidate instanceof HTMLElement &&
      isVisibleGuideTourCloseElement(candidate)
    ) {
      return candidate
    }
  }

  return null
}

/** Discovers close actions scoped to one active modal host. */
export const discoverGuideTourModalCloseActions = (
  host: HTMLElement
): GuideTourModalCloseAction[] => {
  const actions: GuideTourModalCloseAction[] = []
  const hostTag = host.tagName.toLowerCase()

  for (const element of collectCloseClickElements(host)) {
    actions.push({ type: 'click', element })
  }

  if (HEXA_DIALOG_HOSTS.has(hostTag)) {
    actions.push({ type: 'hexa-dialog-close', host })

    const shadowCloseButton = findShadowHeaderCloseButton(host)

    if (shadowCloseButton) {
      actions.push({ type: 'click', element: shadowCloseButton })
    }
  }

  return actions
}

const HEXA_DIALOG_CLOSE_EVENTS = ['hxClickClose', 'hxClickCancel'] as const

const dispatchHexaDialogClose = (host: HTMLElement): void => {
  for (const type of HEXA_DIALOG_CLOSE_EVENTS) {
    host.dispatchEvent(new CustomEvent(type, { bubbles: true, composed: true }))
  }
}

export const executeGuideTourModalCloseAction = (
  action: GuideTourModalCloseAction
): void => {
  logGuideTourDebug('executeGuideTourModalCloseAction', {
    type: action.type,
    tag:
      action.type === 'click'
        ? action.element.tagName.toLowerCase()
        : action.host.tagName.toLowerCase(),
    id:
      action.type === 'click' ? action.element.id : action.host.id || undefined,
  })

  if (action.type === 'hexa-dialog-close') {
    dispatchHexaDialogClose(action.host)
    forceClickGuideTourElement(action.host)
    return
  }

  forceClickGuideTourElement(action.element)
}

/** Closes active modals by notifying React listeners and running DOM fallbacks. */
export const clickGuideTourModalCloseTargets = (): void => {
  requestGuideTourModalsClose()

  const activeHosts = getActiveGuideTourModalHosts()
  const hosts =
    activeHosts.length > 0 ? activeHosts : collectGuideTourModalHosts()

  logGuideTourDebug('clickGuideTourModalCloseTargets', {
    modalStillActive: isAnyGuideTourModalActive(),
    activeHostCount: activeHosts.length,
    hostCount: hosts.length,
    hostIds: hosts.map(host => host.id || host.tagName.toLowerCase()),
  })

  for (const host of hosts) {
    const actions = discoverGuideTourModalCloseActions(host)

    logGuideTourDebug('discoverGuideTourModalCloseActions', {
      hostId: host.id || host.tagName.toLowerCase(),
      actionCount: actions.length,
      actions: actions.map(action =>
        action.type === 'click'
          ? {
              type: action.type,
              tag: action.element.tagName.toLowerCase(),
              id: action.element.id,
            }
          : { type: action.type }
      ),
    })

    for (const action of actions) {
      executeGuideTourModalCloseAction(action)
    }
  }
}

export type GuideTourExitKind = 'completed' | 'skipped' | 'dismissed'

export const getPrecedingGuideTourForceClickStepIndex = (
  tour: GuideTourDefinition,
  stepIndex: number
): number => {
  for (let index = stepIndex - 1; index >= 0; index -= 1) {
    if (tour.steps[index]?.forceClick) {
      return index
    }
  }

  return -1
}

export const wasGuideTourModalOpenedByForceClick = (
  tour: GuideTourDefinition,
  stepIndex: number
): boolean => {
  if (getGuideTourStepModalVisibleGate(tour, stepIndex) === undefined) {
    return false
  }

  const previousStep = stepIndex > 0 ? tour.steps[stepIndex - 1] : undefined

  return Boolean(previousStep?.forceClick)
}

/** True when the user is past a step that opened a modal via forceClick. */
export const isGuideTourStepInForceClickModalFlow = (
  tour: GuideTourDefinition,
  stepIndex: number
): boolean => getPrecedingGuideTourForceClickStepIndex(tour, stepIndex) >= 0

export const shouldCloseGuideTourModalOnExit = (
  tour: GuideTourDefinition,
  stepIndex: number
): boolean =>
  wasGuideTourModalOpenedByForceClick(tour, stepIndex) ||
  isGuideTourStepInForceClickModalFlow(tour, stepIndex)

export const shouldRestoreGuideTourPageScrollOnExit = (
  tour: GuideTourDefinition,
  stepIndex: number
): boolean =>
  stepIndex === tour.steps.length - 1 &&
  isGuideTourStepInForceClickModalFlow(tour, stepIndex)

const restoreGuideTourPageScroll = (scrollOriginY: number): void => {
  window.requestAnimationFrame(() => {
    window.scrollTo({ top: scrollOriginY, left: 0, behavior: 'smooth' })
  })
}

const clickGuideTourModalCloseTargetsWithRetry = (): void => {
  const clickCloseTargets = () => {
    clickGuideTourModalCloseTargets()
  }

  clickCloseTargets()
  queueMicrotask(clickCloseTargets)
  window.requestAnimationFrame(clickCloseTargets)
  window.setTimeout(clickCloseTargets, 0)
}

export const GUIDE_TOUR_MODAL_CLOSE_POLL_MS = 50
export const GUIDE_TOUR_MODAL_CLOSE_TIMEOUT_MS = 2_000

const scheduleGuideTourModalsCloseUntilHidden = (): void => {
  if (typeof window === 'undefined') {
    return
  }

  const startedAt = Date.now()

  logGuideTourDebug('scheduleGuideTourModalsCloseUntilHidden:start', {
    modalActive: isAnyGuideTourModalActive(),
  })

  const attemptCloseWhileModalActive = (): void => {
    const modalActive = isAnyGuideTourModalActive()

    if (!modalActive) {
      logGuideTourDebug('scheduleGuideTourModalsCloseUntilHidden:done', {
        elapsedMs: Date.now() - startedAt,
      })
      return
    }

    clickGuideTourModalCloseTargets()

    if (Date.now() - startedAt >= GUIDE_TOUR_MODAL_CLOSE_TIMEOUT_MS) {
      logGuideTourDebug('scheduleGuideTourModalsCloseUntilHidden:timeout', {
        elapsedMs: Date.now() - startedAt,
        modalActive: isAnyGuideTourModalActive(),
      })
      return
    }

    window.setTimeout(
      attemptCloseWhileModalActive,
      GUIDE_TOUR_MODAL_CLOSE_POLL_MS
    )
  }

  window.setTimeout(
    attemptCloseWhileModalActive,
    GUIDE_TOUR_MODAL_CLOSE_POLL_MS
  )
}

export const shouldRequestGuideTourModalsCloseOnExit = (
  tour: GuideTourDefinition,
  stepIndex: number
): boolean =>
  shouldCloseGuideTourModalOnExit(tour, stepIndex) ||
  isAnyGuideTourModalActive()

export const shouldRestoreGuideTourForcedScrollOnExit = (
  tour: GuideTourDefinition,
  stepIndex: number,
  pathname: string,
  registry: GuideTourRegistry,
  exitKind: GuideTourExitKind
): boolean => {
  if (exitKind !== 'completed') {
    return false
  }

  if (!shouldRestoreGuideTourPageScrollOnExit(tour, stepIndex)) {
    return false
  }

  if (getGuideTourScrollRestoreTarget(pathname) === undefined) {
    return false
  }

  return !hasMoreUnseenRouteToursAfter(pathname, registry, tour)
}

/**
 * Restores programmatic scroll only when Concluir finishes the last pending tour
 * on the route. Always clears recorded scroll state so the next tour does not inherit it.
 */
export const finalizeGuideTourForcedScrollOnExit = (
  tour: GuideTourDefinition,
  stepIndex: number,
  pathname: string,
  registry: GuideTourRegistry,
  exitKind: GuideTourExitKind
): void => {
  if (
    shouldRestoreGuideTourForcedScrollOnExit(
      tour,
      stepIndex,
      pathname,
      registry,
      exitKind
    )
  ) {
    const scrollOriginY = getGuideTourScrollRestoreTarget(pathname)

    if (scrollOriginY !== undefined) {
      restoreGuideTourPageScroll(scrollOriginY)
    }
  }

  clearGuideTourRouteScrollState(pathname)
}

/**
 * Clicks configured modal close targets when leaving a tour step that opened
 * or still shows a modal. Retries while observers detect an active modal host.
 */
export const closeGuideTourModalIfOpenedByForceClick = (
  tour: GuideTourDefinition,
  stepIndex: number
): void => {
  const shouldClose = shouldRequestGuideTourModalsCloseOnExit(tour, stepIndex)

  logGuideTourDebug('closeGuideTourModalIfOpenedByForceClick', {
    tourId: tour.id,
    stepIndex,
    shouldClose,
    inModalFlow: isGuideTourStepInForceClickModalFlow(tour, stepIndex),
    modalActive: isAnyGuideTourModalActive(),
  })

  if (!shouldClose) {
    return
  }

  clickGuideTourModalCloseTargetsWithRetry()
  scheduleGuideTourModalsCloseUntilHidden()
}
